import React, { Fragment, useEffect, useState } from "react";
import { Box, Card, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { Heart, Trash, UsersFour } from "@phosphor-icons/react";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { GenericIconButton } from "../../../components/generic/GenericIconButton";
import { GenericDivider } from "../../../components/generic/GenericDivider";
import { GenericIconLabel } from "../../../components/generic/GenericIconLabel";
import { getSavedProfiles } from "../../../config/apisauce";
import useStore from "../../../states/store";
import { useCreateUserPreference } from "../../../components/generic/functions/genericFunctions";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import { GenericLinearLoader } from "../../../components/generic/GenericLoader";
import { INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { toSlug } from "../../../helpers/stringHelper";

export const SavedProfiles = () => {
  const [{ userPreference }, { setUserPreference }] = useStore();
  const createUserPreference = useCreateUserPreference();
  const [savedProfiles, setSavedProfiles] = useState([]);
  const isDisabled = savedProfiles?.length === 0;
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ per_page: 10 });

  const fetchSavedProfiles = async () => {
    setLoading(true);
    const response = await getSavedProfiles({
      ...INITIAL_GET_PARAMS,
      ...pagination,
      user_id: userPreference?.saved_profiles
    });
    if (response.ok) {
      setSavedProfiles(response?.data);
    }
    setLoading(false);
  };

  const handleEmptyList = () => {
    createUserPreference(true, userPreference?.saved_profiles);
    setUserPreference({ ...userPreference, saved_profiles: [] });
    setSavedProfiles([]);
  };

  const handleScroll = (event) => {
    const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
    if (bottom && !loading) {
      setPagination((prev) => ({
        ...prev,
        per_page: savedProfiles?.per_page + 10
      }));
    }
  };

  useEffect(() => {
    if (userPreference?.saved_profiles?.length > 0) fetchSavedProfiles();
  }, [userPreference, pagination]);

  return (
    <Card sx={{ height: "100%"}}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={loading ? '5px' : '16px'}>
        <Typography variant="h6" color="primary" fontWeight={700}>Liked Profiles ({savedProfiles?.total ?? 0})</Typography>
        <GenericIconLabel
          color={isDisabled ? 'grey.light' : 'primary.main'}
          typographyProps={{ fontWeight: 700 }}
          sx={{ cursor: !isDisabled ? 'pointer' : 'default' }}
          icon={<Trash size={16} weight="bold" />}
          label={'Empty List'}
          onClick={() => !isDisabled && handleEmptyList()}
        />
      </Stack>
      {loading && <GenericLinearLoader margin={'0px 0px 9px 0px'} />}
      {
        savedProfiles?.data?.length > 0 ? (
          <Box
            {...(savedProfiles?.per_page < savedProfiles?.total && { onScroll: handleScroll })}
            sx={{
              overflowX: 'auto',
              paddingRight: savedProfiles?.data?.length > 3 ? 1 : 0,
            }}
          >
          {
            savedProfiles?.data?.map((profile, index) => {
              return (
                <Fragment key={`profile-key-${index}`}>
                  <Grid container justifyContent={'space-between'} alignItems={'center'}>
                    <Grid container item gap={2} alignItems={'center'} onClick={() => window.open(`/creator?user=${toSlug(profile?.name)}-${profile?.id}`, '_blank')} sx={{ cursor: 'pointer' }}>
                      <Grid item>
                        <GenericAvatar size={'51px'} location={profile?.details?.profile_picture?.location} />
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" fontWeight={700} noWrap>{profile?.name}</Typography>
                        <Typography variant="subtitle1" color="text.light" noWrap width={{ xs: 200, md: '100%' }}>{profile?.details?.profession}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <GenericIconButton
                        color={'error.main'}
                        onClick={() => {
                          if (userPreference?.saved_profiles?.length > 1) {
                            createUserPreference(true, profile?.id);
                          } else {
                            handleEmptyList();
                          }
                        }}
                        icon={<Heart size={24} weight={'fill'} />}
                      />
                    </Grid>
                  </Grid>
                  {index !== (savedProfiles?.data?.length - 1) && <GenericDivider margin={'20px 0'} />}
                </Fragment>
              );
            })
          }
          </Box>
        ) : (
          <GenericNoData
            icon={<UsersFour size={40} />}
            label={'No saved profiles'}
            height={"100%"}
          />
        )
      }
    </Card>
  );
};